<template>
    <div id="custom-multiselect" :class="{ 'show-feedback': isInvalid }">
        <vue-multiselect
            :id="id"
            v-model="model"
            label="label"
            trackBy="value"
            :options="options"
            openDirection="bottom"
            :hide-selected="hideSelected"
            :close-on-select="closeOnSelect"
            :multiple="multiple"
            :disabled="disabled"
            :class="{ 'is-valid': isValid, 'is-invalid': isInvalid }"
            :placeholder="placeholder"
            :tagPlaceholder="$t('form.multiselect.tagPlaceholder')"
            :selectLabel="$t('form.multiselect.selectLabel')"
            :selectGroupLabel="$t('form.multiselect.selectGroupLabel')"
            :selectedLabel="$t('form.multiselect.selectedLabel')"
            :deselectLabel="$t('form.multiselect.deselectLabel')"
            :deselectGroupLabel="$t('form.multiselect.deselectGroupLabel')"
            :allowEmpty="allowEmpty"
            :showLabels="showLabels"
            :internal-search="internalSearch"
            :loading="loading"
            :clear-on-select="clearOnSelect"
            @input="changedSelectedValue()"
            @search-change="changeSearch"
            @open="$emit('click', $event)"
        >
            <template slot="singleLabel" slot-scope="{ option }">
                <div class="d-flex align-items-center">
                    <avatar :size="28" slot="aside" class="mr-2" :src="option.picture" :username="option.owner" />
                    <div class="d-flex flex-column">
                        <div>
                            <strong>
                                {{ option.label }}
                            </strong>
                        </div>
                        <small v-if="option.occupation" class="text-muted">
                            {{ option.occupation }}
                        </small>
                        <small class="text-muted">
                            <div>
                                <strong>{{ $t('feed.fields.user_id') }}: </strong>{{ option.owner }}
                            </div>
                            <div>
                                <strong>{{ $t('feed.fields.period_id') }}: </strong>{{ option.period }}
                            </div>
                        </small>
                    </div>
                    <div class="ml-auto">
                        <img
                            v-b-tooltip.hover
                            :title="$t(option.section.name == null ? 'groupLevel.personal' : option.section.name)"
                            :src="levelIcon(option.level)"
                        />
                    </div>
                </div>
            </template>
            <template slot="option" slot-scope="{ option }">
                <div class="d-flex align-items-center">
                    <avatar :size="28" slot="aside" class="mr-2" :src="option.picture" :username="option.owner" />
                    <div class="d-flex flex-column">
                        <div class="mb-1">
                            <span>{{ option.label }}</span>
                        </div>
                        <small v-if="option.occupation" class="text-muted">
                            {{ option.occupation }}
                        </small>
                        <small class="text-muted">
                            <div>
                                <strong>{{ $t('feed.fields.user_id') }}: </strong>{{ option.owner }}
                            </div>
                            <div>
                                <strong>{{ $t('feed.fields.period_id') }}: </strong>{{ option.period }}
                            </div>
                        </small>
                    </div>
                    <div class="ml-auto mr-3">
                        <img
                            v-b-tooltip.hover
                            :title="$t(option.section.name == null ? 'groupLevel.personal' : option.section.name)"
                            :src="levelIcon(option.level)"
                        />
                    </div>
                </div>
            </template>
            <template slot="tag" slot-scope="{ option, remove }">
                <div class="d-flex align-items-center mb-1">
                    <avatar :size="28" slot="aside" class="mr-2" :src="option.picture" :username="option.owner" />
                    <div class="d-flex flex-column">
                        <div>
                            <strong>
                                {{ option.label }}
                            </strong>
                        </div>
                        <small v-if="option.occupation" class="text-muted">
                            {{ option.occupation }}
                        </small>
                    </div>
                    <div
                        style="cursor: pointer"
                        class="ml-auto mr-2"
                        @keypress.enter.prevent="remove(option)"
                        @mousedown.prevent="remove(option)"
                    >
                        <i class="fas fa-backspace" />
                    </div>
                </div>
            </template>
            <template slot="afterList">
                <b-button
                    v-if="nextPageButton.isShowNextPage"
                    :disabled="nextPageButton.loading || nextPageButton.isLastPage"
                    @click="$emit('next-page')"
                    class="mt-1 mb-1"
                    variant="primary"
                    block
                >
                    <span v-if="!nextPageButton.isLastPage && !nextPageButton.loading">{{
                        $t('form.multiselect.showMore')
                    }}</span>
                    <span v-if="nextPageButton.isLastPage && !nextPageButton.loading">{{
                        $t('form.multiselect.showingAllResults')
                    }}</span>
                    <i v-if="nextPageButton.loading" class="fa fa-spinner fa-spin px-2" />
                </b-button>
            </template>
            <span slot="noResult">{{ noElementsFound }}</span>
            <template slot="noOptions">
                {{ noOptionsFound }}
            </template>
        </vue-multiselect>
    </div>
</template>

<script>
import Avatar from 'vue-avatar'
import VueMultiselect from 'vue-multiselect'
import i18n from '@/i18n'

export default {
    name: 'SelectWithAvatar',
    components: {
        Avatar,
        VueMultiselect,
    },
    props: {
        id: {
            type: String,
            default: '',
        },
        options: {
            type: Array,
            default: () => {
                return []
            },
        },
        state: {
            type: Boolean,
            default: null,
        },
        selected: {
            type: [Object, Array],
            default: null,
        },
        hideSelected: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: i18n.t('form.placeholder.select'),
        },
        allowEmpty: {
            type: Boolean,
            default: true,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        closeOnSelect: {
            type: Boolean,
            default: false,
        },
        clearOnSelect: {
            type: Boolean,
            default: true,
        },
        showLabels: {
            type: Boolean,
            default: false,
        },
        internalSearch: {
            type: Boolean,
            default: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        noElementsFound: {
            type: String,
            default: i18n.t('form.multiselect.noElementsFound'),
        },
        noOptionsFound: {
            type: String,
            default: '',
        },
        nextPageButton: {
            type: Object,
            default: () => {
                return {
                    isShowNextPage: false,
                    isLastPage: false,
                    loading: false,
                }
            },
        },
    },
    data() {
        return {
            model: null,
        }
    },
    computed: {
        isValid() {
            return this.state && this.state !== null
        },
        isInvalid() {
            return !this.state && this.state !== null
        },
    },
    watch: {
        selected(value) {
            this.model = value
        },
    },
    methods: {
        changedSelectedValue() {
            this.$emit('changeValue', this.model)
            this.$emit('update:selected', this.model)
        },
        changeSearch(query) {
            this.$emit('changeSearch', query)
        },
        levelIcon(level) {
            let icon = ''
            switch (level) {
                case 'unit':
                    icon = 'unit'
                    break
                case 'department':
                    icon = 'department'
                    break
                default:
                    icon = 'personal'
                    break
            }
            return require(`@/assets/icons/${icon}.svg`)
        },
    },
}
</script>

<style lang="scss" scope>
@import '@/scss/CustomMultiselect.scss';
</style>

<style>
.show-feedback ~ .invalid-feedback {
    display: block !important;
}
</style>
